import {
  LayoutDashboard, Activity, Dumbbell, Heart,
  Stethoscope, Apple, User, Settings, Upload, UserCheck, FileText, Table2, Star, CalendarDays,
  Wrench, Compass, Fingerprint, BarChart3, Home as HomeIcon, Trophy, GitCompare
} from 'lucide-react';
import { SSG_COMPETITION_NAME } from './ssgConstants';

// Trainer Ratings and 360 Team Table moved out of this main tab bar on 2026-07-22 — they're
// only reachable via Home's own nav bar now (src/components/PageChrome.js's TopBar), which
// carries the same isAdmin/allowedTabs gating those two entries had here.
export const ALL_TABS = [
  { icon: LayoutDashboard, label: 'Team Overview',     id: 'overview',      section: 'dashboard' },
  { icon: Activity,        label: 'GPS & Load',        id: 'gps',           section: 'dashboard' },
  { icon: CalendarDays,    label: 'Week Schedule',     id: 'weekschedule',  section: 'dashboard' },
  { icon: Dumbbell,        label: 'Physical Testing',  id: 'testing',       section: 'dashboard' },
  { icon: Heart,           label: 'Readiness',         id: 'readiness',     section: 'dashboard' },
  { icon: Stethoscope,     label: 'Medical',           id: 'medical',       section: 'dashboard' },
  { icon: Apple,           label: 'Nutrition',         id: 'nutrition',     section: 'dashboard' },
  { icon: UserCheck,       label: 'Availability',      id: 'availability',  section: 'dashboard' },
  { icon: User,            label: 'Player Profiles',   id: 'players',       section: 'dashboard' },
  { icon: LayoutDashboard, label: 'Wellness Overview', id: 'wellnessstaff', section: 'dashboard' },
  { icon: FileText,        label: 'Forms',             id: 'forms',         section: 'dashboard' },
  // Period League (2026-08-11) — moved here from HOME_ONLY_TABS so it shows up in the main tab
  // bar (visible on every "dashboard" page, e.g. Physical Testing) instead of only being
  // reachable through Home's own nav (PageChrome's TopBar). Home's TopBar still carries its own
  // pill for it too (see PageChrome.js's NAV_ITEMS) — this just adds a second entry point that
  // doesn't require 'home' access first. Same 'ssg' id as before, so existing allowedTabs grants
  // keep working unchanged.
  { icon: Trophy,          label: SSG_COMPETITION_NAME, id: 'ssg',          section: 'dashboard' },
  { icon: Upload,          label: 'Upload zone',       id: 'gpsupload',     section: 'input' },
  { icon: Dumbbell,        label: 'Test Input',        id: 'testinput',     section: 'input' },
  { icon: Settings,        label: 'Settings',          id: 'settings',      section: 'dashboard' },
];

// Team Table and Trainer Ratings: gated the same way (PageChrome.js's TopBar checks
// user.isAdmin || allowedTabs.includes(id)) but reachable only from Home's own nav bar, not
// this file's old tab bar — so they're a separate list rather than folded into ALL_TABS above,
// which would reintroduce them as duplicate buttons in that old bar (App.js's visibleTabs
// derives straight from ALL_TABS). Their entries were dropped entirely when they moved out of
// the old bar on 2026-07-22, which silently broke Settings' admin UI: there was no checkbox
// for either id, so no non-admin account could ever be granted access to them. Fixed
// 2026-07-25 — Settings.js merges this list in alongside ALL_TABS for its own Tab Access
// checklist only.
// Squad Builder (Stage 7b) joined this same "reachable only via PageChrome's TopBar" situation
// but was left deliberately ungated when it launched — no pre-existing tabs.js/allowedTabs
// entry to carry forward, unlike Team Table/Trainer Ratings above. Added here 2026-07-29 so
// Settings' Tab Access grid can gate it like everything else (see PageChrome.js's NAV_ITEMS,
// flipped gated: false -> true to match).
// Home itself (2026-07-29) — was unconditionally reachable for every account ("the landing
// lobby everyone gets", per App.js's old comment); now gated the same as everything else here.
// Confirmed default: only admins keep it automatically (isAdmin bypasses allowedTabs, same
// rule as always) plus whichever specific accounts (e.g. Vincent) are explicitly granted it —
// everyone else's landing page falls back to Team Overview ('overview'), enforced in App.js's
// AppShell where `activePage` is initialized. New accounts do NOT get 'home' by default even
// though every other tab does (Settings.js special-cases it out of TAB_OPTIONS.map(...) there).
export const HOME_ONLY_TABS = [
  { icon: HomeIcon, label: 'Home',                id: 'home',                 section: 'home' },
  // Individual report (2026-08-12) — was ungated since it was first built (never lived in any
  // tabs.js list, just an unconditional button in PageChrome's TopBar — see that file's NAV_ITEMS
  // comment), which meant anyone with 'home' access saw it regardless of their own allowedTabs.
  // Now managed independently, same gate as Team Table/Trainer Ratings: flips PageChrome's
  // gated: false -> true to match. Existing non-admin accounts lose the previously-automatic
  // access until explicitly re-granted here — same tradeoff 'home' itself took on 2026-07-29.
  { icon: User,     label: 'Individual Report',   id: 'individualreport',     section: 'home' },
  { icon: Table2,   label: 'Team Table',          id: 'team360table',         section: 'home' },
  { icon: Star,     label: 'Trainer Ratings',     id: 'trainerratings',       section: 'home' },
  { icon: Wrench,   label: 'Squad Builder',       id: 'lineup-builder',       section: 'home' },
  // Reached via a button on Team Table (2026-08-05), not the old tab bar — same "no pre-existing
  // tabs.js entry" situation as Squad Builder, added here up front this time so Settings' Tab
  // Access grid has a checkbox for it from day one instead of needing a follow-up fix.
  { icon: BarChart3, label: 'Individual Progress', id: 'individual-progress', section: 'home' },
  // Reached via a button on Technical Overview (2026-08-23), not the old tab bar — same
  // "no pre-existing tabs.js entry" situation as Individual Progress above, added here up front
  // so Settings' Tab Access grid has a checkbox for it from day one.
  { icon: GitCompare, label: 'Technical Comparison', id: 'technical-comparison', section: 'home' },
];

// Tactical/Identity (Stage 6a) and Technical (Stage 6b) team-overview pages — reached only via
// Home's own pillar bubbles (src/pages/Home.js's bubbleLinks), not PageChrome's TopBar, so a
// third list rather than folded into HOME_ONLY_TABS above. Never had a tabs.js entry at all
// until now, which meant Settings had no checkbox for them and Home's bubbles linked through
// unconditionally for every account regardless of allowedTabs — the exact same class of gap
// HOME_ONLY_TABS was fixed for on 2026-07-25, just never caught for these three. Fixed
// 2026-07-29 — see Home.js's bubbleLinks for the actual gating check.
export const PILLAR_BUBBLE_TABS = [
  { icon: Compass,     label: 'Tactical Overview', id: 'tactical-overview',  section: 'home' },
  { icon: Fingerprint, label: 'Identity Overview',  id: 'identity-overview',  section: 'home' },
  { icon: BarChart3,   label: 'Technical Overview', id: 'technical-overview', section: 'home' },
];
